import React, { useState, useEffect, useCallback } from 'react';
import { productsApi, categoriesApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Package, ArrowLeft, Loader2, Search, AlertTriangle, Copy, Languages } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import ProductFormDialog, { initFormData } from './Products/ProductFormDialog';
import { SortableList } from './SortableList';

const BADGE_OPTIONS = [
  { key: 'badge_artesano', icon: '🔨' }, { key: 'badge_local', icon: '📍' },
  { key: 'badge_ecologico', icon: '🌿' }, { key: 'badge_edicion_limitada', icon: '⏳' },
  { key: 'badge_premium', icon: '💎' }, { key: 'badge_recomendado', icon: '👍' },
  { key: 'badge_natural', icon: '🍃' }, { key: 'badge_sin_alcohol', icon: '🚫' },
  { key: 'badge_vegano', icon: '🌱' }, { key: 'badge_sin_gluten', icon: '🌾' },
  { key: 'badge_bestseller', icon: '⭐' }, { key: 'badge_nuevo', icon: '✨' },
];

const ProductsPanel = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSeccion, setFilterSeccion] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [batchTranslating, setBatchTranslating] = useState(false);

  const [formData, setFormData] = useState(() => initFormData(null, []));

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productsApi.getAll({ limit: 500, active_only: false }),
        categoriesApi.getAll({ active_only: false })
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenDialog = (product = null) => {
    setEditingProduct(product);
    setFormData(initFormData(product, categories));
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.es) { toast.error('El nombre en español es obligatorio'); return; }
    if (!formData.price || formData.price <= 0) { toast.error('El precio debe ser mayor que 0'); return; }

    setSaving(true);
    try {
      const dataToSave = { ...formData, images: formData.image_url ? [formData.image_url] : [] };
      if (editingProduct) {
        await productsApi.update(editingProduct.id, dataToSave);
        toast.success('Producto actualizado');
      } else {
        await productsApi.create(dataToSave);
        toast.success('Producto creado');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Error al guardar');
    } finally { setSaving(false); }
  };

  // Inline toggle for enabling/disabling a product without opening the form.
  // Optimistic UI: flip the switch immediately, roll back if the API fails.
  const [togglingIds, setTogglingIds] = useState(new Set());
  const handleToggleActive = useCallback(async (product, nextActive) => {
    if (!product) return;
    setTogglingIds(prev => { const s = new Set(prev); s.add(product.id); return s; });
    // Optimistic local update: mutate the products list in place so the row
    // reflects the new state before the round-trip finishes.
    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_active: nextActive } : p));
    try {
      await productsApi.update(product.id, { is_active: nextActive });
      toast.success(nextActive ? 'Activado en la carta' : 'Oculto de la carta');
    } catch (err) {
      // Rollback on failure
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_active: !nextActive } : p));
      toast.error('No se pudo actualizar');
    } finally {
      setTogglingIds(prev => { const s = new Set(prev); s.delete(product.id); return s; });
    }
  }, []);

  const handleDuplicate = (product) => {
    const dupeData = initFormData(product, categories);
    dupeData.name = { ...dupeData.name, es: `${dupeData.name.es} (copia)` };
    setEditingProduct(null);
    setFormData(dupeData);
    setShowDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await productsApi.delete(deleteTarget.id);
      toast.success('Producto eliminado');
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.success('Producto ya eliminado');
        setDeleteTarget(null);
        fetchData();
      } else {
        console.error('Error deleting product:', error);
        toast.error('Error al eliminar');
      }
    } finally {
      setDeleting(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name?.es || categoryId || 'Sin categoría';
  };

  const handleBatchTranslate = async () => {
    const token = localStorage.getItem('admin_token');
    setBatchTranslating(true);
    toast.info('Traduciendo todo... esto puede tardar unos segundos');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/translate/batch`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        const msg = `${data.categories_translated} categorías y ${data.products_translated} productos traducidos`;
        toast.success(msg);
        fetchData();
      } else {
        toast.error(data.detail || 'Error al traducir');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setBatchTranslating(false);
    }
  };

  const handleReorderProducts = async (categoryId, newItems) => {
    setProducts(prev => {
      const others = prev.filter(p => p.category_id !== categoryId);
      return [...others, ...newItems];
    });
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/reorder/batch`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ order: newItems.map((item, idx) => ({ id: item.id, sort_order: idx })) })
      });
      toast.success('Orden guardado');
    } catch (e) {
      toast.error('Error al reordenar');
      fetchData();
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = !searchTerm || 
      p.name?.es?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.name?.en?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || p.category_id === filterCategory;
    const matchesSeccion = !filterSeccion || p.seccion === filterSeccion;
    return matchesSearch && matchesCategory && matchesSeccion;
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto pb-24">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/admin-menu')} className="rounded-lg">
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl md:text-2xl font-medium text-gray-800">Productos</h1>
          <p className="text-sm text-gray-500">{filteredProducts.length} de {products.length} productos</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-green-600 hover:bg-green-700 rounded-lg" data-testid="new-product-btn">
          <Plus size={18} className="mr-1" />
          <span className="hidden sm:inline">Nuevo</span>
        </Button>
        <Button
          variant="outline"
          onClick={handleBatchTranslate}
          disabled={batchTranslating}
          className="rounded-lg gap-1.5"
          data-testid="batch-translate-products-btn"
        >
          {batchTranslating ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} />}
          <span className="hidden sm:inline">Traducir todo</span>
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Buscar producto..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-product" />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-[150px]"
          data-testid="filter-category"
        >
          <option value="">Todas las categorías</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name?.es || cat.id}</option>
          ))}
        </select>
        <select
          value={filterSeccion}
          onChange={(e) => setFilterSeccion(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white min-w-[120px]"
          data-testid="filter-seccion"
        >
          <option value="">Todas las secciones</option>
          <option value="barra">🍸 Barra</option>
          <option value="cocina">🔥 Cocina</option>
          <option value="tienda">🛒 Tienda</option>
        </select>
      </div>

      {/* Products List — grouped by category for drag & drop ordering */}
      <div className="space-y-6">
        {(() => {
          // Group filtered products by category
          const grouped = {};
          filteredProducts.forEach(p => {
            const cid = p.category_id || '_sin_categoria';
            if (!grouped[cid]) grouped[cid] = [];
            grouped[cid].push(p);
          });
          const groupKeys = Object.keys(grouped);
          
          if (groupKeys.length === 0) {
            return (
              <div className="text-center py-12 text-gray-500">
                <Package size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No se encontraron productos</p>
              </div>
            );
          }

          return groupKeys.map(cid => {
            const catName = getCategoryName(cid);
            const prods = grouped[cid];
            return (
              <div key={cid}>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2 px-1">
                  {catName} ({prods.length})
                </h3>
                <SortableList
                  items={prods}
                  onReorder={(newItems) => handleReorderProducts(cid, newItems)}
                  renderItem={(product) => (
                    <Card className="border border-gray-100" data-testid={`product-row-${product.id}`}>
                      <CardContent className="p-3 md:p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {(product.image_url || product.images?.[0]) ? (
                              <img src={product.image_url || product.images?.[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Package size={20} className="text-gray-400" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-800 truncate">{product.name?.es || product.id}</p>
                              {product.seccion && (
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium whitespace-nowrap ${
                                  product.seccion === 'barra' ? 'bg-purple-100 text-purple-700' :
                                  product.seccion === 'cocina' ? 'bg-orange-100 text-orange-700' :
                                  'bg-teal-100 text-teal-700'
                                }`}>
                                  {product.seccion === 'barra' ? 'Barra' : product.seccion === 'cocina' ? 'Cocina' : 'Tienda'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-wrap">
                              {product.supplements?.length > 0 && (
                                <span className="text-xs text-blue-500">+{product.supplements.length} extras</span>
                              )}
                              {BADGE_OPTIONS.filter(b => product[b.key]).slice(0, 4).map(badge => (
                                <span key={badge.key} className="text-xs">{badge.icon}</span>
                              ))}
                            </div>
                          </div>
                          
                          <div className="text-right flex-shrink-0">
                            <p className="font-semibold text-gray-800">{product.price?.toFixed(2)}€</p>
                            {/* Inline enable/disable toggle — no need to open the product form to hide it from the menu. */}
                            <div className="flex items-center justify-end gap-1.5 mt-1" onClick={(e) => e.stopPropagation()}>
                              <Switch
                                checked={product.is_active !== false}
                                onCheckedChange={(checked) => handleToggleActive(product, checked)}
                                disabled={togglingIds.has(product.id)}
                                data-testid={`toggle-active-${product.id}`}
                                className="data-[state=checked]:bg-green-500"
                              />
                              <span className={`text-[11px] ${product.is_active !== false ? 'text-green-600' : 'text-red-500'}`}>
                                {product.is_active !== false ? 'Activo' : 'Oculto'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" onClick={() => handleDuplicate(product)} className="h-11 w-11 sm:h-9 sm:w-9 p-0" data-testid={`duplicate-product-${product.id}`} title="Duplicar">
                              <Copy size={18} className="text-blue-400" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(product)} className="h-11 w-11 sm:h-9 sm:w-9 p-0" data-testid={`edit-product-${product.id}`}>
                              <Edit2 size={18} className="text-gray-500" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(product)} className="h-11 w-11 sm:h-9 sm:w-9 p-0 hover:text-red-600" data-testid={`delete-product-${product.id}`}>
                              <Trash2 size={18} className="text-red-400" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                />
              </div>
            );
          });
        })()}
      </div>

      {/* Form Dialog */}
      <ProductFormDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        saving={saving}
        editingProduct={editingProduct}
        categories={categories}
        allProducts={products}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} />
              Eliminar producto
            </DialogTitle>
            <DialogDescription>
              Esta accion no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <p className="text-gray-700">
              ¿Seguro que quieres eliminar <strong>{deleteTarget?.name?.es || 'este producto'}</strong>?
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="flex-1 h-12 sm:h-10 sm:flex-none">
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="flex-1 h-12 sm:h-10 sm:flex-none bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 size={16} className="animate-spin mr-1" /> : <Trash2 size={16} className="mr-1" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsPanel;
